'use client';

import React from 'react';

import { Button, type ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { UI_CONFIG } from '@eliadmin/core/src/constants/ui-config';
import { cn, mergeClassObjects } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';

export type CarouselControlsProps = {
  className?: {
    main?: string;
    prev?: string;
    next?: string;
    button?: string;
  };
  buttonProps?: ButtonProps;
  prevIcon?: React.ReactNode;
  nextIcon?: React.ReactNode;
};

export const CarouselControls = (props: CarouselControlsProps) => {
  const { t } = useTranslations();
  const className = mergeClassObjects(props.className, UI_CONFIG.carouselControls?.className);
  const buttonProps = props.buttonProps ?? UI_CONFIG.carouselControls?.buttonProps;
  const prevIcon = props.prevIcon ?? UI_CONFIG.carouselControls?.prevIcon ?? (
    <RiArrowLeftSLine size={20} />
  );
  const nextIcon = props.nextIcon ?? UI_CONFIG.carouselControls?.nextIcon ?? (
    <RiArrowRightSLine size={20} />
  );

  const context = useCarouselContext();
  if (!context) throw new Error('CarouselControls must be used inside CarouselProvider');

  const hasNavigation = context.canGoPrev || context.canGoNext;

  return (
    <div
      aria-hidden={!hasNavigation}
      className={cn(
        'flex flex-row gap-2',
        'ea-carousel-controls',
        !hasNavigation && 'invisible',
        className.main,
      )}
    >
      <Button
        {...buttonProps}
        aria-label={t('text3525')}
        onClick={() => context.api?.scrollPrev()}
        disabled={!context.canGoPrev}
        className={cn(
          'h-8 w-11 sm:h-12 sm:w-[68px]',
          'ea-carousel-controls__button ea-carousel-controls__button--prev',
          className.button,
          className.prev,
        )}
        variant={buttonProps?.variant ? buttonProps.variant : 'secondary'}
      >
        {prevIcon}
      </Button>
      <Button
        {...buttonProps}
        aria-label={t('text3526')}
        onClick={() => context.api?.scrollNext()}
        disabled={!context.canGoNext}
        className={cn(
          'h-8 w-11 sm:h-12 sm:w-[68px]',
          'ea-carousel-controls__button ea-carousel-controls__button--next',
          className.button,
          className.next,
        )}
        variant={buttonProps?.variant ? buttonProps.variant : 'secondary'}
      >
        {nextIcon}
      </Button>
    </div>
  );
};
